/**
 * @element qds-row-actions
 *
 * The action affordance at the end of a table row: up to a few inline icon
 * buttons, everything else behind an overflow menu.
 *
 * Inline actions are revealed on row hover and on focus-within, NOT always
 * visible. A column of repeated icons down 200 rows is visual noise. The
 * overflow trigger stays visible so the affordance is never hidden.
 *
 * Destructive actions are NEVER inline, regardless of `inline-count`. A delete
 * button next to a duplicate button, revealed on hover, in a 200-row table,
 * will destroy data.
 *
 * @attr {string} row-id - Echoed in every event detail. Default ''.
 * @attr {number} inline-count - How many non-destructive actions render inline; 0 = all in menu. Default 2.
 * @attr {('sm'|'md'|'lg')} size - Matches row density. Default 'md'.
 * @attr {boolean} disabled - Disables the whole group (all buttons and the menu).
 * @attr {boolean} always-visible - Skips hover-reveal; inline actions always visible.
 * @attr {boolean} open - Reflected; overflow menu open state.
 *
 * @property {QdsRowAction[]} actions - Array of action objects (property only, not an attribute).
 * @property {QdsTableRow} row - Populated by qds-data-table when it owns the component; standalone use leaves undefined.
 * @property {boolean} open - Overflow menu open state (reflected to attribute).
 *
 * @csspart container - The outer wrapper.
 * @csspart inline-action - Each inline action button.
 * @csspart overflow-trigger - The overflow menu trigger button.
 * @csspart menu - The overflow menu panel.
 * @csspart menu-item - Each menu item.
 * @csspart menu-item-destructive - Destructive menu items (also carries menu-item).
 * @csspart menu-divider - Divider between groups or before destructive items.
 * @csspart menu-icon - Icon within a menu item.
 * @csspart menu-label - Label text within a menu item.
 *
 * @fires qds-row-action - When an action is activated. detail: {component, action, rowId, row}.
 * @fires qds-open - When the overflow menu opens. detail: {component, rowId}.
 * @fires qds-close - When the overflow menu closes. detail: {component, rowId}.
 *
 * @example
 * <qds-row-actions row-id="row-1" inline-count="2"></qds-row-actions>
 * <script>
 *   const el = document.querySelector('qds-row-actions');
 *   el.actions = [
 *     { id: 'edit', label: 'Edit', icon: 'pi-pencil' },
 *     { id: 'duplicate', label: 'Duplicate', icon: 'pi-clone' },
 *     { id: 'export', label: 'Export', icon: 'pi-download' },
 *     { id: 'delete', label: 'Delete', icon: 'pi-trash', destructive: true }
 *   ];
 *   el.addEventListener('qds-row-action', (e) => {
 *     console.log('Action:', e.detail.action, 'Row:', e.detail.rowId);
 *   });
 * </script>
 */

import { loadBaseCSS, adoptPrimeIconsStyles } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons, renderQdsIcon } from '../shared/icon-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import type { QdsRowAction, QdsTableRow } from '../../types/index.js';

let instanceCounter = 0;

export class QdsRowActions extends HTMLElement {
  private _actions: QdsRowAction[] = [];
  private _rowId: string = '';
  private _inlineCount: number = 2;
  private _size: 'sm' | 'md' | 'lg' = 'md';
  private _disabled: boolean = false;
  private _alwaysVisible: boolean = false;
  private _open: boolean = false;
  private _row: QdsTableRow | undefined;
  private _menuId: string;
  private _cleanups: (() => void)[] = [];
  private _docListenersAttached: boolean = false;

  private _boundDocClick: (e: Event) => void;
  private _boundDocOpen: (e: Event) => void;
  private _boundMenuKeyDown: (e: Event) => void;
  private _boundContainerClick: (e: Event) => void;

  static get observedAttributes(): string[] {
    return ['row-id', 'inline-count', 'size', 'disabled', 'always-visible', 'open'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
    this._menuId = `qds-row-actions-menu-${++instanceCounter}`;

    this._boundDocClick = this._handleDocClick.bind(this);
    this._boundDocOpen = this._handleDocOpen.bind(this);
    this._boundMenuKeyDown = this._handleMenuKeyDown.bind(this);
    this._boundContainerClick = this._handleContainerClick.bind(this);
  }

  // ── Properties ────────────────────────────────────────────────────────

  /** Array of action objects. Property only — not reflected to an attribute. */
  get actions(): QdsRowAction[] {
    return this._actions;
  }

  set actions(value: QdsRowAction[]) {
    this._actions = Array.isArray(value) ? value : [];
    if (this._open) this._closeMenu();
    if (this.isConnected) this._render();
  }

  /** Populated by qds-data-table; undefined for standalone use. */
  get row(): QdsTableRow | undefined {
    return this._row;
  }

  set row(value: QdsTableRow | undefined) {
    this._row = value;
  }

  /** Overflow menu open state (reflected to the `open` attribute). */
  get open(): boolean {
    return this._open;
  }

  set open(value: boolean) {
    const v = Boolean(value);
    if (v === this._open) return;
    if (v) this._openMenu();
    else this._closeMenu();
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;
    upgradeProperty(this, 'actions');
    upgradeProperty(this, 'row');
    this._rowId = this.getAttribute('row-id') || '';
    this._inlineCount = this._hasInlineCountAttr() ? this._parseInlineCount(this.getAttribute('inline-count')) : 2;
    const sizeAttr = this.getAttribute('size');
    this._size = (sizeAttr === 'sm' || sizeAttr === 'lg') ? sizeAttr : 'md';
    this._disabled = this.hasAttribute('disabled');
    this._alwaysVisible = this.hasAttribute('always-visible');
    this._open = this.hasAttribute('open');
    this._render();
  }

  disconnectedCallback(): void {
    this._cleanupListeners();
    this._removeDocumentListeners();
    this._open = false;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'row-id':
        this._rowId = newValue || '';
        break;
      case 'inline-count':
        this._inlineCount = newValue !== null ? this._parseInlineCount(newValue) : 2;
        if (this._open) this._closeMenu();
        break;
      case 'size':
        this._size = (newValue === 'sm' || newValue === 'lg') ? newValue : 'md';
        break;
      case 'disabled':
        this._disabled = newValue !== null;
        if (this._disabled && this._open) this._closeMenu();
        break;
      case 'always-visible':
        this._alwaysVisible = newValue !== null;
        break;
      case 'open': {
        const isOpen = newValue !== null;
        if (isOpen !== this._open) {
          if (isOpen) this._openMenu();
          else this._closeMenu();
        }
        return; // _openMenu/_closeMenu handles re-render
      }
    }

    if (this.isConnected) this._render();
  }

  // ── Action splitting ──────────────────────────────────────────────────

  /** Non-destructive actions that render as inline icon buttons. */
  private _getInlineActions(): QdsRowAction[] {
    const nonDestructive = this._actions.filter(a => !a.destructive);
    return nonDestructive.slice(0, this._inlineCount);
  }

  /** Actions that go into the overflow menu: non-destructive overflow + destructive at end. */
  private _getOverflowActions(): QdsRowAction[] {
    const nonDestructive = this._actions.filter(a => !a.destructive);
    const destructive = this._actions.filter(a => a.destructive);
    return [...nonDestructive.slice(this._inlineCount), ...destructive];
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  private _render(): void {
    if (!this.shadowRoot) return;

    this._cleanupListeners();

    const inlineActions = this._getInlineActions();
    const overflowActions = this._getOverflowActions();
    const hasOverflow = overflowActions.length > 0;
    const triggerLabel = this._rowId ? `More actions for ${this._rowId}` : 'More actions';

    let inlineHtml = '';
    for (const action of inlineActions) {
      const isDisabled = !!action.disabled || this._disabled;
      const disabledAttr = isDisabled ? 'disabled' : '';
      const titleAttr = isDisabled && action.disabledReason ? `title="${this._escapeAttr(action.disabledReason)}"` : '';
      const iconHtml = renderQdsIcon(action.icon, 'inline-icon');
      inlineHtml += `<button class="inline-action" part="inline-action" data-action-id="${this._escapeAttr(action.id)}" aria-label="${this._escapeAttr(action.label)}" ${disabledAttr} ${titleAttr}>${iconHtml}</button>`;
    }

    let triggerHtml = '';
    if (hasOverflow) {
      const triggerDisabled = this._disabled ? 'disabled' : '';
      triggerHtml += `<button class="overflow-trigger" part="overflow-trigger" aria-haspopup="menu" aria-expanded="${this._open}" aria-controls="${this._menuId}" aria-label="${this._escapeAttr(triggerLabel)}" ${triggerDisabled}><i class="pi pi-ellipsis-v" aria-hidden="true"></i></button>`;
    }

    let menuHtml = '';
    if (hasOverflow) {
      const menuHidden = this._open ? '' : 'hidden';
      menuHtml += `<div class="menu" part="menu" role="menu" id="${this._menuId}" ${menuHidden}>${this._renderMenuItems(overflowActions)}</div>`;
    }

    this.shadowRoot.innerHTML = `
      <style>${this._getStyles()}</style>
      <div class="container" part="container">
        ${inlineHtml}
        ${triggerHtml}
        ${menuHtml}
      </div>
    `;

    // Attach listeners
    const container = this.shadowRoot.querySelector('.container');
    if (container) {
      container.addEventListener('click', this._boundContainerClick);
      this._cleanups.push(() => container.removeEventListener('click', this._boundContainerClick));
    }

    const menu = this.shadowRoot.querySelector('.menu');
    if (menu) {
      menu.addEventListener('keydown', this._boundMenuKeyDown);
      this._cleanups.push(() => menu.removeEventListener('keydown', this._boundMenuKeyDown));
    }

    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);
  }

  private _renderMenuItems(overflowActions: QdsRowAction[]): string {
    let html = '';
    let prevGroup: string | undefined;
    let seenDestructiveDivider = false;

    for (const action of overflowActions) {
      // Insert divider before first destructive item
      if (action.destructive && !seenDestructiveDivider) {
        html += '<div class="menu-divider" role="separator" part="menu-divider"></div>';
        seenDestructiveDivider = true;
        prevGroup = undefined; // reset to avoid double divider
      }

      // Insert divider between named groups (not at start/end, not adjacent to destructive divider)
      if (
        prevGroup !== undefined &&
        action.group !== undefined &&
        action.group !== prevGroup
      ) {
        html += '<div class="menu-divider" role="separator" part="menu-divider"></div>';
      }

      const isDestructive = !!action.destructive;
      const isDisabled = !!action.disabled || this._disabled;
      const partAttr = isDestructive ? 'menu-item menu-item-destructive' : 'menu-item';
      const classAttr = isDestructive ? 'menu-item destructive' : 'menu-item';
      const ariaDisabled = isDisabled ? 'aria-disabled="true"' : '';
      const titleAttr = isDisabled && action.disabledReason ? `title="${this._escapeAttr(action.disabledReason)}"` : '';
      const accessibleName = isDisabled && action.disabledReason
        ? `${action.label} (${action.disabledReason})`
        : action.label;
      const iconHtml = renderQdsIcon(action.icon, 'menu-icon', 'menu-icon');

      html += `<div class="${classAttr}" role="menuitem" part="${partAttr}" data-action-id="${this._escapeAttr(action.id)}" ${ariaDisabled} ${titleAttr} aria-label="${this._escapeAttr(accessibleName)}" tabindex="-1">${iconHtml}<span class="menu-label" part="menu-label">${this._escapeText(action.label)}</span></div>`;

      prevGroup = action.group;
    }

    return html;
  }

  // ── Menu open/close ───────────────────────────────────────────────────

  private _openMenu(): void {
    if (this._open || this._disabled) return;
    this._open = true;
    this.setAttribute('open', '');
    this._addDocumentListeners();
    // Notify other instances to close
    document.dispatchEvent(new CustomEvent('qds-row-actions-opening', {
      detail: { component: this }
    }));
    this._render();
    this._dispatchOpenClose('qds-open');
    this._focusFirstMenuItem();
  }

  private _closeMenu(): void {
    if (!this._open) return;
    this._open = false;
    this.removeAttribute('open');
    this._removeDocumentListeners();
    this._render();
    this._dispatchOpenClose('qds-close');
  }

  // ── Event dispatch ────────────────────────────────────────────────────

  private _dispatchAction(actionId: string): void {
    this.dispatchEvent(new CustomEvent('qds-row-action', {
      bubbles: true,
      composed: true,
      detail: {
        component: this,
        action: actionId,
        rowId: this._rowId,
        row: this._row
      }
    }));
  }

  private _dispatchOpenClose(eventName: string): void {
    this.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: {
        component: this,
        rowId: this._rowId
      }
    }));
  }

  // ── Event handlers ────────────────────────────────────────────────────

  private _handleContainerClick(e: Event): void {
    if (this._disabled) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    let el: HTMLElement | null = target;
    while (el) {
      // Overflow trigger
      if (el.classList && el.classList.contains('overflow-trigger')) {
        e.preventDefault();
        e.stopPropagation();
        if (this._open) this._closeMenu();
        else this._openMenu();
        return;
      }

      // Inline action or menu item
      const actionId = el.getAttribute('data-action-id');
      if (actionId) {
        if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') {
          return; // Disabled — do nothing
        }
        e.preventDefault();
        e.stopPropagation();
        this._dispatchAction(actionId);
        if (this._open) this._closeMenu();
        return;
      }

      el = el.parentElement;
    }
  }

  private _handleMenuKeyDown(e: Event): void {
    const ke = e as KeyboardEvent;
    if (!this._open) return;

    const menu = this.shadowRoot?.querySelector('.menu');
    if (!menu) return;

    const allItems = Array.from(menu.querySelectorAll('[role="menuitem"]')) as HTMLElement[];
    const enabledItems = allItems.filter(item => item.getAttribute('aria-disabled') !== 'true');

    if (enabledItems.length === 0) {
      if (ke.key === 'Escape') {
        ke.preventDefault();
        this._closeMenu();
        this._focusTrigger();
      }
      return;
    }

    const currentFocused = this.shadowRoot?.activeElement as HTMLElement | null;
    let currentIndex = enabledItems.findIndex(item => item === currentFocused);

    switch (ke.key) {
      case 'ArrowDown':
        ke.preventDefault();
        currentIndex = (currentIndex + 1) % enabledItems.length;
        enabledItems[currentIndex].focus();
        break;
      case 'ArrowUp':
        ke.preventDefault();
        currentIndex = (currentIndex - 1 + enabledItems.length) % enabledItems.length;
        enabledItems[currentIndex].focus();
        break;
      case 'Home':
        ke.preventDefault();
        enabledItems[0].focus();
        break;
      case 'End':
        ke.preventDefault();
        enabledItems[enabledItems.length - 1].focus();
        break;
      case 'Escape':
        ke.preventDefault();
        this._closeMenu();
        this._focusTrigger();
        break;
      case 'Enter':
      case ' ':
        ke.preventDefault();
        if (currentIndex >= 0) {
          const item = enabledItems[currentIndex];
          const actionId = item.getAttribute('data-action-id');
          if (actionId) {
            this._dispatchAction(actionId);
            this._closeMenu();
            this._focusTrigger();
          }
        }
        break;
    }
  }

  private _handleDocClick(e: Event): void {
    // When the click is inside this component's shadow DOM, the event target
    // is retargeted to the host element (this) by the time it reaches document.
    // When the click is outside, the target is some other node.
    let node: Node | null = e.target as Node;
    while (node) {
      if (node === this) return; // click is inside — do nothing
      node = node.parentNode;
    }
    // click is outside — close
    this._closeMenu();
  }

  private _handleDocOpen(e: Event): void {
    const detail = (e as CustomEvent).detail;
    if (detail && detail.component !== this && this._open) {
      this._closeMenu();
    }
  }

  // ── Focus helpers ─────────────────────────────────────────────────────

  private _focusFirstMenuItem(): void {
    const menu = this.shadowRoot?.querySelector('.menu');
    if (!menu) return;
    const firstEnabled = menu.querySelector('[role="menuitem"]:not([aria-disabled="true"])') as HTMLElement | null;
    if (firstEnabled) {
      firstEnabled.focus();
    }
  }

  private _focusTrigger(): void {
    const trigger = this.shadowRoot?.querySelector('.overflow-trigger') as HTMLElement | null;
    if (trigger) {
      trigger.focus();
    }
  }

  // ── Document listener management ──────────────────────────────────────

  private _addDocumentListeners(): void {
    if (this._docListenersAttached) return;
    document.addEventListener('click', this._boundDocClick);
    document.addEventListener('qds-row-actions-opening', this._boundDocOpen);
    this._docListenersAttached = true;
  }

  private _removeDocumentListeners(): void {
    if (!this._docListenersAttached) return;
    document.removeEventListener('click', this._boundDocClick);
    document.removeEventListener('qds-row-actions-opening', this._boundDocOpen);
    this._docListenersAttached = false;
  }

  // ── Listener cleanup ──────────────────────────────────────────────────

  private _cleanupListeners(): void {
    for (const cleanup of this._cleanups) {
      cleanup();
    }
    this._cleanups = [];
  }

  // ── Utilities ─────────────────────────────────────────────────────────

  private _hasInlineCountAttr(): boolean {
    return this.hasAttribute('inline-count');
  }

  private _parseInlineCount(value: string | null): number {
    if (value === null) return 2;
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : 2;
  }

  private _escapeAttr(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private _escapeText(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── Styles ────────────────────────────────────────────────────────────

  private _getStyles(): string {
    return `
      :host {
        display: inline-flex;
        position: relative;
        align-items: center;
        justify-content: flex-end;
        --qds-row-actions-button-size: 32px;
        --qds-row-actions-gap: var(--theme-space-1, 4px);
        --qds-row-actions-menu-min-width: 180px;
        --qds-row-actions-destructive-color: var(--theme-error, #8B1F00);
      }

      :host([size="sm"]) {
        --qds-row-actions-button-size: 28px;
      }

      .container {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--qds-row-actions-gap, var(--theme-space-1, 4px));
        position: relative;
      }

      /* ── Inline action buttons ── */

      .inline-action {
        width: var(--qds-row-actions-button-size, 32px);
        height: var(--qds-row-actions-button-size, 32px);
        min-width: var(--qds-row-actions-button-size, 32px);
        min-height: var(--qds-row-actions-button-size, 32px);
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: var(--theme-radius-sm, 4px);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        color: var(--icon-color-secondary, var(--theme-text-secondary, #555555));
        opacity: 0;
        transition: opacity var(--transition-speed-fast, 0.15s),
                    background-color var(--transition-speed-fast, 0.15s);
      }

      .inline-action i {
        font-size: var(--icon-size, 20px);
        color: currentColor;
      }

      .inline-action:hover {
        background-color: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
      }

      .inline-action:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .inline-action[disabled] {
        cursor: not-allowed;
      }

      /* ── Hover / focus-within reveal ── */

      :host(:hover) .inline-action,
      :host(:focus-within) .inline-action,
      :host([always-visible]) .inline-action {
        opacity: 1;
      }

      :host(:hover) .inline-action[disabled],
      :host(:focus-within) .inline-action[disabled],
      :host([always-visible]) .inline-action[disabled] {
        opacity: 0.5;
      }

      /* ── Overflow trigger ── */

      .overflow-trigger {
        width: var(--qds-row-actions-button-size, 32px);
        height: var(--qds-row-actions-button-size, 32px);
        min-width: var(--qds-row-actions-button-size, 32px);
        min-height: var(--qds-row-actions-button-size, 32px);
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: var(--theme-radius-sm, 4px);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        color: var(--icon-color-secondary, var(--theme-text-secondary, #555555));
        transition: background-color var(--transition-speed-fast, 0.15s);
      }

      .overflow-trigger i {
        font-size: var(--icon-size, 20px);
        color: currentColor;
      }

      .overflow-trigger:hover {
        background-color: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
      }

      .overflow-trigger:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .overflow-trigger[disabled] {
        cursor: not-allowed;
        opacity: 0.5;
      }

      /* ── Menu ── */

      .menu {
        position: absolute;
        top: 100%;
        right: 0;
        min-width: var(--qds-row-actions-menu-min-width, 180px);
        background: var(--theme-background-overlay, var(--theme-background-primary, #FFFFFF));
        border: 1px solid var(--theme-border-primary, #E4E9F2);
        border-radius: var(--theme-radius-md, 8px);
        box-shadow: var(--theme-shadow-md, 0 8px 24px rgba(44, 51, 73, 0.1));
        padding: var(--theme-space-1, 4px) 0;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        margin-top: var(--theme-space-1, 4px);
      }

      .menu[hidden] {
        display: none;
      }

      /* ── Menu items ── */

      .menu-item {
        padding: var(--theme-space-2, 8px) var(--theme-space-3, 12px);
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: var(--theme-space-2, 8px);
        color: var(--theme-text-primary, #333333);
        font-size: var(--theme-font-size-sm, 14px);
        font-family: var(--theme-font-family, sans-serif);
        white-space: nowrap;
        transition: background-color var(--transition-speed-fast, 0.15s);
      }

      .menu-item:hover {
        background-color: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
      }

      .menu-item:focus-visible {
        outline: none;
        box-shadow: inset 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .menu-item.destructive {
        color: var(--qds-row-actions-destructive-color, var(--theme-error, #8B1F00));
      }

      .menu-item.destructive:hover {
        background-color: var(--theme-background-error-subtle, rgba(139, 31, 0, 0.10));
      }

      .menu-item[aria-disabled="true"] {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .menu-item[aria-disabled="true"]:hover {
        background-color: transparent;
      }

      .menu-icon {
        font-size: var(--icon-size, 20px);
        color: currentColor;
      }

      .menu-label {
        flex: 1;
      }

      /* ── Menu dividers ── */

      .menu-divider {
        height: 1px;
        background-color: var(--theme-border-primary, #E4E9F2);
        margin: var(--theme-space-1, 4px) 0;
      }

      /* ── Coarse pointer: always visible, 44x44 ── */

      @media (pointer: coarse) {
        :host {
          --qds-row-actions-button-size: 44px;
        }

        .inline-action {
          opacity: 1;
        }

        .inline-action[disabled] {
          opacity: 0.5;
        }
      }
    `;
  }
}

if (!customElements.get('qds-row-actions')) {
  customElements.define('qds-row-actions', QdsRowActions);
}
